import { memo, useCallback, type ClipboardEvent, type SyntheticEvent } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useTypedSelector } from "hooks";
import { selectCountries } from "features/general/selectors";
import { getFormInputProps } from "util/forms";
import {
    parseBulkTokens,
    isBulkPaste,
    resolveCountryTokens,
    resolveCurrencyTokens,
    mergeUnique
} from "util/bulkPaste";
import Autocomplete, { CheckboxAutocomplete } from "ui/atoms/Autocomplete";
import { Checkbox } from "ui/forms/formik-elements";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import { FormField, type SetupPaymentMethodBasicInfoProps } from "./types";

const BasicInfo = ({
    isBootstrapDataLoading,
    bootstrapData,
    isSubmitting,
    isValid,
    values,
    resetForm,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    ...restProps
}: SetupPaymentMethodBasicInfoProps) => {
    const isEditing = 'coreId' in restProps.initialValues;
    const countries = useTypedSelector(selectCountries());

    const onAutocompleteChange = (formField: FormField) =>
        (_: SyntheticEvent, values: Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    const applyBulkValues = (formField: FormField, nextValues: string[]) => {
        setFieldValue(formField, nextValues, true);
        setTimeout(() => setFieldTouched(formField, true));
    };

    const handleCountryPaste = useCallback((event: ClipboardEvent<HTMLDivElement>) => {
        const text = event.clipboardData.getData('text');
        if (!isBulkPaste(text)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const tokens = parseBulkTokens(text);
        const { matched } = resolveCountryTokens(tokens, countries);
        if (!matched.length) {
            return;
        }

        const current = (values[FormField.ForCountries] as string[]) ?? [];
        applyBulkValues(FormField.ForCountries, mergeUnique(current, matched));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countries, values[FormField.ForCountries]]);

    const handleCurrencyPaste = useCallback((event: ClipboardEvent<HTMLDivElement>) => {
        const text = event.clipboardData.getData('text');
        if (!isBulkPaste(text)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const tokens = parseBulkTokens(text);
        const { matched } = resolveCurrencyTokens(tokens, bootstrapData[FormField.ForCurrencies]);
        if (!matched.length) {
            return;
        }

        const current = (values[FormField.ForCurrencies] as string[]) ?? [];
        applyBulkValues(FormField.ForCurrencies, mergeUnique(current, matched));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bootstrapData, values[FormField.ForCurrencies]]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'flex-end'
            }}
            component='form'
            onSubmit={handleSubmit}
            autoComplete='off'
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1
                }}
            >
                <TextField
                    {...getFormInputProps(FormField.Name, restProps)}
                    size="small"
                    name={FormField.Name}
                    value={values[FormField.Name]}
                    label='Method Name'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    required
                />
                <CountryIso2
                    {...restProps}
                    fullWidth
                    multiple
                    value={values[FormField.ForCountries]}
                    onChange={onAutocompleteChange(FormField.ForCountries)}
                    TextFieldProps={{
                        ...getFormInputProps(FormField.ForCountries, restProps),
                        required: true,
                        name: FormField.ForCountries,
                        label: 'Countries Available',
                        onPaste: handleCountryPaste
                    }}
                />
                <TextField
                    {...getFormInputProps(FormField.DayToHoldOfflineFunds, restProps)}
                    size="small"
                    name={FormField.DayToHoldOfflineFunds}
                    value={values[FormField.DayToHoldOfflineFunds]}
                    label='Days To Hold Funds'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                />
            </Box>
            <Box
                sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1
                }}
            >
                <TextField
                    {...getFormInputProps(FormField.Tag, restProps)}
                    size="small"
                    name={FormField.Tag}
                    value={values[FormField.Tag]}
                    label='Method Tag'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isEditing}
                    fullWidth
                    required
                />
                <CheckboxAutocomplete
                    fullWidth
                    multiple
                    size='small'
                    value={values[FormField.ForCurrencies]}
                    loading={isBootstrapDataLoading}
                    TextFieldProps={{
                        ...getFormInputProps(FormField.ForCurrencies, restProps),
                        required: true,
                        name: FormField.ForCurrencies,
                        label: 'Currencies Available',
                        onPaste: handleCurrencyPaste
                    }}
                    onChange={onAutocompleteChange(FormField.ForCurrencies)}
                    FormControlProps={{
                        sx: {
                            width: '100%'
                        }
                    }}
                    options={bootstrapData[FormField.ForCurrencies]}
                />
                <Autocomplete
                    fullWidth
                    disableClearable
                    size='small'
                    layout={null}
                    loading={isBootstrapDataLoading}
                    disabled={isBootstrapDataLoading || isEditing}
                    options={bootstrapData.PSP}
                    value={values[FormField.Provider]}
                    onChange={onAutocompleteChange(FormField.Provider)}
                    TextFieldProps={{
                        ...getFormInputProps(FormField.Provider, restProps),
                        required: true,
                        name: FormField.Provider,
                        label: 'Provider'
                    }}
                />
                <Checkbox
                    label='Method can be available for merchant'
                    handleBlur={handleBlur}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    CheckboxProps={{
                        inputProps: {
                            'aria-label': 'Method can be available for merchant'
                        },
                        name: FormField.IsAssignable,
                        checked: values[FormField.IsAssignable]
                    }}
                />
                <Checkbox
                    label='Activate send payment request email'
                    handleBlur={handleBlur}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    CheckboxProps={{
                        inputProps: {
                            'aria-label': 'Activate send payment request email'
                        },
                        name: FormField.SendPaymentRequestMail,
                        checked: values[FormField.SendPaymentRequestMail]
                    }}
                />
                <Checkbox
                    label='Activate send payment receipt email'
                    handleBlur={handleBlur}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    CheckboxProps={{
                        inputProps: {
                            'aria-label': 'Activate send payment receipt email'
                        },
                        name: FormField.SendPaymentReceiptMail,
                        checked: values[FormField.SendPaymentReceiptMail]
                    }}
                />
                <Checkbox
                    label='Method requires Spec-In Parameters'
                    handleBlur={handleBlur}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    CheckboxProps={{
                        inputProps: {
                            'aria-label': 'Method requires Spec-In Parameters'
                        },
                        name: FormField.RequireSpecIn,
                        checked: values[FormField.RequireSpecIn]
                    }}
                />
                <Checkbox
                    label='Refund supported'
                    handleBlur={handleBlur}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                    CheckboxProps={{
                        inputProps: {
                            'aria-label': 'Method has refund support'
                        },
                        name: FormField.IsRefundable,
                        checked: values[FormField.IsRefundable]
                    }}
                />
            </Box>
        </Box>
    );
};

export default memo(BasicInfo);
